import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Image } from 'semantic-ui-react';
import { ParentContext } from '../../cnr/contexts/ParentContext';
import { startConfirmationTypes, startTypes } from '../../cnr/reducers/StartReducer';
import { uniqueKey } from '../../common/keys';
import { gEnums } from '../../enums/globalEnums';
import { _dimmerTypes } from '../../motions/AnimateComponent';
import MotionComponent from '../../motions/MotionComponent';
import { getElementStyle } from '../../styles/formatting';
import { useStyles } from 'global/cnr/contexts/useHelpers/useStyles';

const Splash = ({
  formData,
  styleAndClass: snc,
  show = true,
}) => {

  const { setStyleAndClass } = useStyles()

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, handlers } = parentContext ?? {};
  const {
    currentSettings_state,
    transition_state,
    start_state,
    storage_state,
  } = states ?? {};
  const { start_handlers } = handlers ?? {};

  const { appStorageUrlFiles } = storage_state ?? {};
  const { splash } = appStorageUrlFiles ?? {};
  const firstSplashImage = splash ? _.last(_.values(splash)) : {};
  const { full: splashUrl } = firstSplashImage ?? {};

  // startContext
  const { startConfirmations } = start_state ?? {};
  const { handleAmmend_startDimmer } = start_handlers ?? {};

  // pageContext
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});

  const { themedItems, splashScreen } = aps_global ?? {};
  const {
    useSplashScreenLogo,
    splashScreenLogo,
    splashScreenImage,
    useSplashScreenImage,
  } = splashScreen ?? {};

  const { splashScreen: splashScreenTheme } = themedItems ?? {};
  const { topMenu: topMenu_app } = aps_global ?? {};
  const { mainFixedCaption } = topMenu_app ?? {};

  // transition
  const { dimmers } = transition_state ?? {};
  const dimmer_page = dimmers
    ? dimmers[_dimmerTypes.splash]
    : null;

  const { useDimmerTransition } = dimmer_page ?? {};

  // ------------------------------------------------------------
  // Logo loading
  // ------------------------------------------------------------

  const [logoLoaded, setLogoLoaded] = useState(false);

  useEffect(() => {

    // Firebase/state hasn't supplied the URL yet
    if (!splashUrl) {
      setLogoLoaded(false);
      return;
    }

    // Preload the image before rendering Semantic UI <Image>
    const img = new window.Image();

    img.onload = () => {
      setLogoLoaded(true);
    };

    img.onerror = () => {
      // Don't leave the splash stuck forever if the image fails
      setLogoLoaded(true);
    };

    img.src = splashUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };

  }, [splashUrl]);

  // ------------------------------------------------------------
  // Existing readiness logic
  // ------------------------------------------------------------

  const requiredConfirmations = Object.values(startConfirmationTypes);

  const isAppReady = useMemo(() => {
    return _.every(
      requiredConfirmations,
      key => startConfirmations?.[key] === true
    );
  }, [startConfirmations]);

  // Parent can now control the splash with `show`.
  // Keep this only if the existing start system still needs
  // to be notified when initialization is complete.
  useEffect(() => {
    if (isAppReady && handleAmmend_startDimmer) {
      handleAmmend_startDimmer(startTypes.finish, 'Splash');
    }
  }, [isAppReady, handleAmmend_startDimmer]);

  // ------------------------------------------------------------
  // Styling
  // ------------------------------------------------------------

  const _styleAndClass = setStyleAndClass({ item: gEnums.projectStyles.splashScreen })

  const styleAndClass = snc
    ? { ...snc }
    : { ..._styleAndClass };

  const itemStyle = getElementStyle(
    splashScreenTheme,
    ['fontSize']
  );

  const splashScreen_current = formData
    ? formData
    : splashScreen;

  let {
    useSplashScreenText,
    splashScreenText,
    splashScreenSubText,
  } = splashScreen_current ?? {};

  // useSplashScreenText = false

  const ssText =
    useSplashScreenText && splashScreenText
      ? splashScreenText
      : mainFixedCaption;

  const ssSubText = splashScreenSubText
    ? splashScreenSubText
    : null;

  const hasLogo = !!splashUrl;

  // ------------------------------------------------------------
  // Build classes without mutating settings
  // ------------------------------------------------------------

  const className = [
    styleAndClass.className,
    formData && 'preview',
    hasLogo && 'logo',
    ssSubText && 'foot',
    hasLogo && !useSplashScreenText && 'logo-only',
  ]
    .filter(Boolean)
    .join(' ');

  const splashStyleAndClass = {
    ...styleAndClass,
    className,
  };

  // ------------------------------------------------------------
  // Content
  // ------------------------------------------------------------

  const logo = () => {

    // Do NOT render the Semantic UI Image until
    // the browser has already loaded the image.
    if (!splashUrl || !logoLoaded) {
      return null;
    }

    return (
      <Image
        key={uniqueKey('spl', 'i')}
        centered
        src={splashUrl}
        alt="image"
      />
    );
  };

  const sub = () => (
    ssSubText ? ssSubText : ''
  );

  const splashContent = () => (
    <>
      {useSplashScreenText && <div
        className="splash-text"
        style={itemStyle}
      >
        {ssText}
      </div>}

      <div className="splash-logo">
        {logo()}
      </div>

      {useSplashScreenText && <div className="splash-footer">
        {sub()}
      </div>}
    </>
  );

  // ------------------------------------------------------------
  // Full-screen splash
  // ------------------------------------------------------------

  const content = (
    <div {...splashStyleAndClass}>
      <div className="splash-content">
        {ssText && splashContent()}
      </div>
    </div>
  );

  // Parent controls whether it exists visually.
  if (!show) {
    return null;
  }

  return (
    <MotionComponent
      dimmer={dimmer_page}
      styleAndClass={splashStyleAndClass}
    >
      {content}
    </MotionComponent>
  );

};


export default Splash